import { Injectable } from '@angular/core';
import { Cargochief } from '@service/cargochief/cargochief.service';
import {
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { TRAILER_TYPES_DB } from '@domain/constants/trailer-types';
import { UrlService } from '@service/url/url.service';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { PICKUP_DATE_TYPES_DB } from '@domain/constants/pickup-date-types';

@Injectable({
	providedIn: 'root',
})
export class UserSettingsService {
	constructor(
		private cargochief: Cargochief,
		private urlService: UrlService,
		private restService: RestService,
		private loginSetupService: LoginSetupService
	) {}

	get SEARCHABLE_TRAILER_TYPES(): Array<any> {
		let trailerTypes: Array<any> = [],
			accountSettings: any = this.cargochief.getAccountSettings();
		if (
			IS_NOT_NULL_OR_UNDEFINED(accountSettings) &&
			IS_NOT_EMPTY(accountSettings.searchableTrailerTypes)
		) {
			accountSettings.searchableTrailerTypes.split(',').map((trailer) => {
				trailerTypes.push({ dbVal: trailer.trim().toUpperCase(), value: trailer });
			});
		} else {
			Object.keys(TRAILER_TYPES_DB).forEach((key) => {
				trailerTypes.push({ dbVal: key, value: TRAILER_TYPES_DB[key] });
			});
		}
		return trailerTypes;
	}

	get DEFAULT_TRAILER_TYPES(): Array<any> {
		let defaultTrailerTypes: Array<any> = [],
			userSettings: any = this.cargochief.getUserSettings();
		if (
			IS_NOT_NULL_OR_UNDEFINED(userSettings) &&
			IS_NOT_EMPTY(userSettings.DEFAULT_TRAILER_TYPES)
		) {
			userSettings.DEFAULT_TRAILER_TYPES.split(',').map((trailer) => {
				defaultTrailerTypes.push(trailer.trim());
			});
		}
		return defaultTrailerTypes;
	}

	get PICKUP_DATE_TYPES(): Array<any> {
		let pickupDateTypes: Array<any> = [],
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings) && IS_NOT_EMPTY(userSettings.PICK_UP_DATE_TYPES)) {
			userSettings.PICK_UP_DATE_TYPES.split(',').map((pickupDate) => {
				pickupDateTypes.push({ dbVal: pickupDate.trim().toUpperCase(), value: pickupDate });
			});
		} else {
			Object.entries(PICKUP_DATE_TYPES_DB).forEach(([key, value]) => {
				pickupDateTypes.push({ dbVal: key, value: value });
			});
		}
		return pickupDateTypes;
	}

	get MINIMUM_TRUCK_COUNT(): number {
		let minimunTruckCount: number = 0,
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			minimunTruckCount = userSettings.MINIMUM_TRUCK_COUNT;
		}
		return minimunTruckCount;
	}

	get MINIMUM_FREQUENCY(): number {
		let minimunFrequency: number = 0,
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			minimunFrequency = userSettings.MINIMUM_FREQUENCY;
		}
		return minimunFrequency;
	}

	get DEFAULT_PICKUP_DATE_TYPE(): string {
		let defaultPickupDateType: string = 'Today',
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			defaultPickupDateType = userSettings.DEFAULT_PICK_UP_DATE_TYPE;
		}
		return defaultPickupDateType;
	}

	get DEFAULT_CAPACITY_SEARCH_TAB(): string {
		let defaultCapacitySearchTab: string = 'Notes',
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			defaultCapacitySearchTab = userSettings.DEFAULT_CAPACITY_SEARCH_TAB;
		}
		return defaultCapacitySearchTab;
	}

	get SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT(): number {
		let defaultOriginDeadheadLimit: number = 0,
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			defaultOriginDeadheadLimit = userSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT;
		}
		return defaultOriginDeadheadLimit;
	}

	get SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT(): number {
		let defaultDestinationDeadheadLimit: number = 0,
			userSettings: any = this.cargochief.getUserSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			defaultDestinationDeadheadLimit = userSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT;
		}
		return defaultDestinationDeadheadLimit;
	}

	get BOOKING_ASSISTANT(): boolean {
		const userSettings: any = this.cargochief.getUserSettings();
		let booking_assistant: boolean = false;
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			let value = userSettings.BOOKING_ASSISTANT;
			if (this.isValidSettingValue(value)) {
				booking_assistant = Boolean(JSON.parse(value));
			}
		}
		return booking_assistant;
	}

	get HIDE_ENGAGE_CARRIER_DIALOG(): boolean {
		const userSettings: any = this.cargochief.getUserSettings();
		let engageCarrierDialog: boolean = false;
		if (IS_NOT_NULL_OR_UNDEFINED(userSettings)) {
			let value = userSettings.HIDE_ENGAGE_CARRIER_DIALOG;
			if (this.isValidSettingValue(value)) {
				engageCarrierDialog = Boolean(JSON.parse(value));
			}
		}
		return engageCarrierDialog;
	}

	parseNumericValues(settingName: string): number {
		const value = this.getSettingValue(settingName);
		if (this.isValidSettingValue(value)) {
			return Number(JSON.parse(value));
		}
		return null;
	}

	getSettingValue(settingName: string): any {
		const accountSettings: any = this.cargochief.getAccountSettings();
		if (!IS_NOT_NULL_OR_UNDEFINED(accountSettings)) {
			return accountSettings[settingName];
		}
		return null;
	}

	getAccountSettings(): any {
		const loginSetup: any = localStorage.getItem('loginSetup');
		if (IS_NOT_NULL_OR_UNDEFINED(loginSetup) && IS_NOT_EMPTY(loginSetup)) {
			return JSON.parse(loginSetup).userettings;
		}
		return null;
	}

	isValidSettingValue(settingValue: string): boolean {
		return (
			!IS_NULL_OR_UNDEFINED(settingValue) && IS_NOT_EMPTY(settingValue) && settingValue != 'null'
		);
	}
}
